import cloneDeep from 'lodash/cloneDeep';
import { EvaluatedImage, EvaluatedValue, Input, InputId, NodeId, OutputId } from 'web';
import { FF_EXPORT_WORKFLOW_LINEAGE, FF_EXPORT_WORKFLOW_LINEAGE_ENTERPRISE } from '@/consts/featureFlags';
import { useUserStore } from '@/state/user.state';
import { SubscriptionType } from '@/types/shared';
import { IMPORT_NODE_DEFINITION_ID, PROMPT_NODE_ID } from '@/consts/node-types.consts';
import {
  DEFAULT_NODE_WIDTH,
  MEDIA_VIEWPORT_DIMENSION_NODE_TYPES,
  shouldApplyStoredBoxSize,
} from '@/consts/node-ui.consts';
import { mergeRouterNodeDataForInsert } from '@/consts/router-node.defaults';
import { useWorkflowStore } from '@/state/workflow.state';
import { usePromptEditorFocusStore } from '@/state/promptEditorFocus.state';
import { HandleType } from '@/enums/handle-type.enum';
import { NodeType } from '@/enums/node-type.enum';
import { LORA_INPUT_MODELS } from '@/components/Nodes/ModelComponents/consts/lora-input-models.const';
import { KLING_V4_MULTI_PROMPT_MODELS } from '@/components/Nodes/ModelComponents/consts/kling-v4-prompts.const';
import { getModelInputLimitStatus } from '@/components/Nodes/utils/modelInputLimitsUtils';
import { posthog } from '@/providers/posthog';
import { resolveLineageSourceAssetIds, withLineageSourceAssetIds } from '@/utils/lineageSourceAssets';
import { requestFigmaNodeLayerPickerForTargetInput } from '@/components/Nodes/FigmaNode/figmaNodeLayerPickerAutoOpen';
import {
  calculateMultiNodeBoundsWithPadding,
  categorizeNodesGroupStatus,
  convertChildPositionToAbsolute,
} from '@/components/Nodes/utils/nodeUtils';
import {
  fontFromFamilyKey,
  getFontFamilyKey,
  makeWorkspaceFontFaceKey,
  type FontStyleCombination,
} from '@/utils/customFonts.utils';
import { getOrCreateCached, shouldSkipComputedImageSnapshot, type ComputedImageEntry } from '@/utils/flow';
import { log } from '@/logger/logger';
import { getModelIdentifier } from '@/utils/nodeInputValidation';
import { layerInputId, type CompositorNodeV3 } from '@/types/nodes/compositor';
import { MediaToCanvasDragMode } from '@/utils/mediaToCanvasDrag.utils';
import { getAverageY, getRightmostX } from '@/components/Nodes/packUnpack.utils';
import { getHandleId, parseHandleKey } from '../../Nodes/Utils';
import { createNewNode } from '../useGraph';
import { trackNodesAdded } from '../nodeAdded.analytics';
import { FlowGraph, migrateLegacyNodeShapes } from '../FlowGraph';
import { UndoRedoEntry } from '../undoRedo';
import { isCanvasReadOnlyForToolTab } from '../FlowComponents/WorkflowRightToolbar/toolTabReadOnly.store';
import { DEFAULT_GROUP_PADDING } from './NodeGrouping/groups.types';
import { resolveNodePreviewImagePoster } from './nodePreviewImagePosterUrl';
import { ModelBaseView, type DynamicInputConfig } from './ModelBaseView';
import { PromptView } from './PromptView';
import { CompositorView } from './CompositorView';
import { VideoConcatenatorView } from './VideoConcatenatorView';
import { FigmaNodeView, type FigmaNodeHandleType } from './FigmaNodeView';
import { GroupsManagerView } from './NodeGrouping/GroupsManagerView';
import { convertParameterValueToLegacyValue } from './ParameterView/ParameterView';
import {
  type AddImportFromMediaSourceParams,
  buildRemoveMediaFromSourceUndoEntry,
  getFileKindFromMediaSourceNode,
  getTextFromMediaSourceNode,
  isNodeTypeForMediaToImportSource,
} from './mediaToImportFromSource';
import type { BuiltInFont, FontOptions } from 'web';
import type { XYPosition } from 'reactflow';
import type { BaseNodeData, Edge, EdgeId, Node, VideoConcatenatorData } from '@/types/node';
import type { SubflowTemplateData } from '@/types/api/subflowTemplate';
import type { NodeDefinition } from '@/types/api/nodeDefinition';
import type { ImageRef } from '@/types/api/assets';
import type { ModelBaseNodeData } from '@/types/nodes/model';
import type { Connection } from '../FlowComponents/FlowTour/ConnectionContext';
import type { DuplicateDragSession, DuplicateNodeOptions } from '../flowTypes';
import type { MissingFontFaceReplacement } from '@/types/missingWorkflowFonts.types';

const logger = log.getLogger('FlowView');

const warnedOutputTypeLookups = new Set<NodeId>();

const PROMPT_OFFSET_X = 550;
const PROMPT_OFFSET_Y = 0;
const VIDEO_CONCAT_NODE_OFFSET = 40;

type VideoConcatSource = {
  node: Node;
  outputId: OutputId;
};

type VideoConcatSelection = {
  definition: NodeDefinition;
  sources: VideoConcatSource[];
};

export type SourceConnectStatus = 'ok' | 'no-slot' | 'cycle';

export type ConnectResult = {
  primary: SourceConnectStatus;
  siblings: Array<{ sourceNodeId: NodeId; status: SourceConnectStatus }>;
};

export interface DeleteNodesResult {
  preventedGroups?: NodeId[];
}

export interface DeleteNodesOptions {
  /** When true, reconnects predecessors to successors across the removed region (see {@link FlowGraph.removeNodesPreservingBridgingConnections}). */
  preserveBridgingConnections?: boolean;
  /** Applied with this delete, so one undo reverses the graph and the side effect together. */
  alsoUndo?: UndoRedoEntry;
}

/**
 * Exact handle-type match, else a config that lists the type in `acceptTypes` (e.g. World Labs'
 * `image`, which also takes video), else an `Any`-typed config (e.g. Seedance's `reference`, which
 * accepts any media). An `Any` *connection* is deliberately not a wildcard here: with multiple
 * concrete configs it would ambiguously match the first, so it falls through to "no config" (no
 * auto-add).
 */
export function findDynamicInputConfig(
  configs: DynamicInputConfig[],
  connectionHandleType: HandleType,
): DynamicInputConfig | undefined {
  return (
    configs.find((c) => c.handleType === connectionHandleType) ??
    configs.find((c) => c.acceptTypes?.includes(connectionHandleType)) ??
    configs.find((c) => c.handleType === HandleType.Any)
  );
}

export class FlowView {
  private groupsManagerView: GroupsManagerView;

  constructor(private graph: FlowGraph) {
    this.groupsManagerView = new GroupsManagerView(graph);
  }

  /**
   * Valid (weight, italic) pairs for a base font from the loaded WASM graph
   * (`weightSupport` × `italicSupport`), matching compositor behavior.
   */
  getBaseFontStyleCombinations(font: BuiltInFont): FontStyleCombination[] {
    const fontOptions: FontOptions = {
      font: { type: 'built_in', data: font },
      italic: false,
      weight: 400,
      width: 100,
    };
    return (
      this.graph.wasm.call((wasm) => {
        const weights = Array.from(wasm.weightSupport(fontOptions));
        const italicBytes = wasm.italicSupport(fontOptions);
        const italics = Array.from(italicBytes).map((b) => b !== 0);
        const combos: FontStyleCombination[] = [];
        for (const weight of weights) {
          for (const isItalic of italics) {
            combos.push({ weight, isItalic });
          }
        }
        return combos.length > 0 ? combos : [{ weight: 400, isItalic: false }];
      }) ?? [{ weight: 400, isItalic: false }]
    );
  }

  redraw() {
    this.graph.wasm.call((wasm) => wasm.redraw());
  }

  /**
   * Swaps the whole canvas to `nodes`/`edges` as a reversible off-undo (`ongoing`) edit — like
   * {@link insertPreviewTemplates} but a full replace; {@link clearPreview} restores the exact live graph
   * (incl. unsaved edits) via the edit's inverse. Ids are preserved so callers can mark them transient first.
   */
  previewGraph(nodes: Node[], edges: Edge[]): NodeId[] {
    // Clone first: the graph comes from the snapshot query cache and migrateLegacyNodeShapes mutates in place.
    const migratedNodes = cloneDeep(nodes);
    const migratedEdges = cloneDeep(edges);
    const dropEdgeIds = new Set(migrateLegacyNodeShapes(migratedNodes, migratedEdges));
    const cleanNodes = migratedNodes.map((n) => ({ ...n, selected: false }));
    const cleanEdges = migratedEdges.filter((e) => !dropEdgeIds.has(e.id)).map((e) => ({ ...e, selected: false }));

    this.graph.edit(() => {
      const entry = UndoRedoEntry.empty();
      const liveIds = this.graph.store.getNodes().map((n) => n.id);
      if (liveIds.length > 0) {
        entry.add(this.graph.removeNodes(liveIds));
      }
      if (cleanNodes.length > 0) {
        entry.add(this.graph.addNodes(cleanNodes));
        entry.add(this.graph.addEdges(cleanEdges, false));
      }
      return entry;
    }, /* ongoing */ true);
    this.redraw();
    void this.loadResources();
    return cleanNodes.map((n) => n.id);
  }

  /// NODES
  getNode(nodeId: NodeId): Node {
    return this.graph.store.getNode(nodeId);
  }

  getNodeNullable(nodeId: NodeId): Node | undefined {
    return this.graph.store.getNodeNullable(nodeId);
  }

  getNodes(): Node[] {
    return this.graph.store.getNodes();
  }

  /**
   * Replaces missing custom font faces via {@link CompositorView} / WASM (same path as layer property edits).
   * Built-in family keys are written as `built_in`; workspace uploads as `custom`.
   */
  applyMissingWorkspaceFontReplacements(replacements: MissingFontFaceReplacement[]): void {
    const fontsToReplaceMap = new Map(replacements.map((r) => [r.sourceFaceKey, r]));
    for (const node of this.getNodes()) {
      if (node.type !== 'compv3') {
        continue;
      }
      const compositor = node.data as CompositorNodeV3;
      const compositorView = new CompositorView(this.graph, node.id);
      for (const [layerIdStr, layer] of Object.entries(compositor.data.layers)) {
        if (layer.kind.type !== 'text') {
          continue;
        }
        const font = layer.kind.fontOptions.font;

        const faceKey = makeWorkspaceFontFaceKey(
          getFontFamilyKey(font),
          layer.kind.fontOptions.weight,
          layer.kind.fontOptions.italic,
        );
        const rep = fontsToReplaceMap.get(faceKey);
        if (!rep) {
          continue;
        }
        compositorView.setLayerTextFontFace(
          [Number(layerIdStr)],
          fontFromFamilyKey(rep.replacementFontFamilyKey),
          rep.replacementWeight,
          rep.replacementItalic,
        );
      }
    }
  }

  /**
   * Finds a node at the given flow position.
   * Returns the first matching node.
   *
   * @param flowPosition - The position in flow coordinates
   * @returns The node at the position, or undefined if no node is found
   */
  findNodeAtPosition(flowPosition: { x: number; y: number }): Node | undefined {
    const nodes = this.getNodes();
    return nodes.find((node) => {
      const rect = {
        left: node.position.x,
        top: node.position.y,
        right: node.position.x + (node.width ?? 0),
        bottom: node.position.y + (node.height ?? 0),
      };
      return (
        flowPosition.x >= rect.left &&
        flowPosition.x <= rect.right &&
        flowPosition.y >= rect.top &&
        flowPosition.y <= rect.bottom
      );
    });
  }

  addNewNode(newNode: Node, connection?: Connection, additional?: Connection[]): ConnectResult {
    let result: ConnectResult = { primary: 'no-slot', siblings: [] };
    this.graph.edit(() => {
      const undoEntry = this.graph.addNodes([newNode]);
      const intersectingGroupId = this.groupsManagerView.findIntersectingGroup([newNode]);
      if (intersectingGroupId) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup([newNode], intersectingGroupId));
      }
      if (connection?.handleType && connection.handleSide && connection.nodeId && connection.handleId) {
        const primaryResult = this.connectOneSourceEntry(connection, { nodeId: newNode.id });
        if (primaryResult.entry) undoEntry.add(primaryResult.entry);
        const siblings: ConnectResult['siblings'] = [];
        if (primaryResult.status === 'ok') {
          for (const c of additional ?? []) {
            const sibling = this.connectOneSourceEntry(c, { nodeId: newNode.id });
            if (sibling.entry) undoEntry.add(sibling.entry);
            siblings.push({ sourceNodeId: c.nodeId ?? '', status: sibling.status });
          }
        }
        result = { primary: primaryResult.status, siblings };
      }
      return undoEntry;
    });
    this.graph.updateNodeOutputs(newNode.id, false);
    this.graph.loadResources();
    return result;
  }

  /**
   * Resolves the concat definition and each selected node's first video output in selection order.
   * Undefined when any selected node has no video output. A group or sticky note lives only in the
   * store, and `outputIds` throws for an id WASM does not know. The context menu calls this while
   * rendering, so that throw is treated as no video output and Concat videos stays hidden.
   */
  getVideoConcatSelection(selectedNodeIds: NodeId[]): VideoConcatSelection | undefined {
    if (selectedNodeIds.length < 2 || new Set(selectedNodeIds).size !== selectedNodeIds.length) return undefined;

    const nodeTypes = useWorkflowStore.getState().nodeTypes;
    const definition = nodeTypes.find((definition) => definition.type === 'video_concatenator');
    if (!definition) return undefined;

    const sources: VideoConcatSource[] = [];
    for (const nodeId of selectedNodeIds) {
      const node = this.getNodeNullable(nodeId);
      if (!node) return undefined;

      const outputId = this.firstVideoOutputId(nodeId);
      if (!outputId) return undefined;

      sources.push({ node, outputId });
    }

    return { definition, sources };
  }

  /**
   * First video output of a node. See {@link nodePrimaryOutputType}: store-only nodes make
   * `getOutputIds` and `nodeOutputType` throw, and this lookup runs during menu render.
   */
  private firstVideoOutputId(nodeId: NodeId): OutputId | undefined {
    try {
      return this.getOutputIds(nodeId).find((outputId) => {
        return this.nodeOutputType(nodeId, outputId) === HandleType.Video;
      });
    } catch (e) {
      if (!warnedOutputTypeLookups.has(nodeId)) {
        warnedOutputTypeLookups.add(nodeId);
        logger.warn(`Cannot resolve the output type of node ${nodeId}`, e);
      }
      return undefined;
    }
  }

  /** Creates live video connections in one undoable edit without running the workflow. */
  createVideoConcatFromSelection(selectedNodeIds: NodeId[]): Node | undefined {
    const selection = this.getVideoConcatSelection(selectedNodeIds);
    if (!selection) return undefined;
    const { definition, sources } = selection;

    const nodeTypes = useWorkflowStore.getState().nodeTypes;
    const position = this.calculateVideoConcatNodePosition(sources);
    const newNode = createNewNode(definition.id, nodeTypes, position);
    if (!newNode) return undefined;

    const data = newNode.data as VideoConcatenatorData;
    data.input = sources.map((_, index) => ({ inputId: `video_${index + 1}`, input: undefined, order: index }));
    this.graph.edit(() => {
      const entry = this.graph.addNodes([newNode]);
      sources.forEach((_, index) => {
        const key = `video_${index + 1}`;
        entry.add(
          this.graph.addNodeInputHandle(newNode.id, key, {
            id: key,
            description: '',
            label: `Video ${index + 1}`,
            format: 'uri',
            order: index,
            required: false,
            type: HandleType.Video,
          }),
        );
      });
      const groupId = this.groupsManagerView.findIntersectingGroup([newNode]);
      if (groupId) entry.add(this.groupsManagerView.attachNodesToGroup([newNode], groupId));
      const edges = sources.map(({ node, outputId }, index) =>
        this.graph.createEdge(
          node.id,
          newNode.id,
          getHandleId(node.id, 'output', outputId),
          getHandleId(newNode.id, 'input', `video_${index + 1}`),
        ),
      );
      entry.add(this.graph.addEdges(edges));
      return entry;
    });
    this.graph.loadResources();
    this.selectedNodes = [newNode.id];
    return newNode;
  }

  private calculateVideoConcatNodePosition(sources: VideoConcatSource[]): XYPosition {
    const nodesWithAbsolutePosition = sources.map(({ node }) => {
      return { ...node, position: this.getAbsoluteNodePosition(node) };
    });
    return {
      x: getRightmostX(nodesWithAbsolutePosition) + VIDEO_CONCAT_NODE_OFFSET,
      y: getAverageY(nodesWithAbsolutePosition),
    };
  }

  private getAbsoluteNodePosition(node: Node): XYPosition {
    const position = { ...node.position };
    const visited = new Set([node.id]);
    let parentId = node.parentId;
    while (parentId) {
      if (visited.has(parentId)) break; // Cycle detected
      visited.add(parentId);
      const parent = this.getNodeNullable(parentId);
      if (!parent) break;
      position.x += parent.position.x;
      position.y += parent.position.y;
      parentId = parent.parentId;
    }
    return position;
  }

  /**
   * Adds a node from a media source. Move: one `graph.edit()` (add + remove source item), then reload.
   * Duplicate: `addNewNode` only.
   */
  private addNodeFromMediaSourceAtFlowPosition(
    newNode: Node,
    sourceNodeId: NodeId,
    sourceKind: AddImportFromMediaSourceParams['sourceKind'],
    itemIndex: number,
    mode: MediaToCanvasDragMode,
  ): Node {
    if (mode === MediaToCanvasDragMode.Move) {
      this.graph.edit(() => {
        const undoEntry = this.graph.addNodes([newNode]);
        const intersectingGroupId = this.groupsManagerView.findIntersectingGroup([newNode]);
        if (intersectingGroupId) {
          undoEntry.add(this.groupsManagerView.attachNodesToGroup([newNode], intersectingGroupId));
        }
        const removeEntry = buildRemoveMediaFromSourceUndoEntry(this.graph, sourceNodeId, sourceKind, itemIndex);
        if (removeEntry) {
          undoEntry.add(removeEntry);
        }
        return undoEntry;
      });
      this.graph.updateNodeOutputs(newNode.id, false);
      this.graph.updateNodeOutputs(sourceNodeId, false);
      this.graph.loadResources();
    } else {
      this.addNewNode(newNode);
    }
    this.selectedNodes = [];
    return newNode;
  }

  /**
   * Creates a new Import node from one file or model generation. Use `mode`: `duplicate` to copy, `move` to
   * remove from the source in the same undo step as adding the node (import, media iterator, or AI model).
   */
  addImportFromMediaSourceAtFlowPosition(
    sourceNodeId: NodeId,
    params: AddImportFromMediaSourceParams,
  ): Node | undefined {
    const { sourceKind, mode, itemIndex, flowPosition, nodeTypes } = params;
    const node = this.getNodeNullable(sourceNodeId);
    if (!node) {
      return undefined;
    }

    if (!isNodeTypeForMediaToImportSource(node, sourceKind)) {
      return undefined;
    }

    if (this.isDropPositionInLockedGroup(flowPosition)) {
      return undefined;
    }

    const text = getTextFromMediaSourceNode(node, sourceKind, itemIndex);
    if (text !== undefined) {
      const newNode = createNewNode(
        PROMPT_NODE_ID,
        nodeTypes,
        flowPosition,
        { prompt: text, result: { prompt: text } },
        undefined,
        { isClearInitialData: true },
      );
      if (!newNode) {
        return undefined;
      }
      return this.addNodeFromMediaSourceAtFlowPosition(newNode, sourceNodeId, sourceKind, itemIndex, mode);
    }

    const file = getFileKindFromMediaSourceNode(this.graph, node, sourceKind, itemIndex);
    if (!file) {
      return undefined;
    }

    const newNode = createNewNode(
      IMPORT_NODE_DEFINITION_ID,
      nodeTypes,
      flowPosition,
      { files: [cloneDeep(file)], selectedIndex: 0 },
      undefined,
      { isClearInitialData: true },
    );
    if (!newNode) {
      return undefined;
    }

    return this.addNodeFromMediaSourceAtFlowPosition(newNode, sourceNodeId, sourceKind, itemIndex, mode);
  }

  /**
   * Creates a new Prompt node from a history version's plain text at a canvas drop position.
   * Always duplicate-create (never mutates the source node or history). Returns undefined when the
   * drop would land in a locked group or node creation fails.
   */
  addPromptFromHistoryAtFlowPosition(
    promptText: string,
    flowPosition: XYPosition,
    nodeTypes: Parameters<typeof createNewNode>[1],
    options?: { enterJsonMode?: boolean },
  ): Node | undefined {
    if (this.isDropPositionInLockedGroup(flowPosition)) {
      return undefined;
    }

    const newNode = createNewNode(
      PROMPT_NODE_ID,
      nodeTypes,
      flowPosition,
      { prompt: promptText, result: { prompt: promptText } },
      undefined,
      { isClearInitialData: true },
    );
    if (!newNode) {
      return undefined;
    }

    this.addNewNode(newNode);
    if (options?.enterJsonMode) {
      this.graph.requestPromptEnterJsonMode(newNode.id);
    }
    this.selectedNodes = [];
    return newNode;
  }

  /** Drag-start lock is enforced in node UI; this covers canvas drops that would attach into a locked group. */
  private isDropPositionInLockedGroup(flowPosition: XYPosition): boolean {
    const dropProbe: Node = {
      id: 'media-drop-position-probe',
      type: NodeType.Import,
      position: flowPosition,
      width: DEFAULT_NODE_WIDTH,
      parentId: undefined,
      locked: false,
      data: {} as BaseNodeData,
    };
    const intersectingGroupId = this.groupsManagerView.findIntersectingGroup([dropProbe]);
    return intersectingGroupId != null && this.getEffectiveIsLocked(intersectingGroupId);
  }

  /**
   * Adds multiple nodes and wraps them in a named group as a single undo operation.
   * Skips auto-attachment to existing groups so positions stay consistent.
   */
  addNodesAsGroup(newNodes: Node[], groupName: string) {
    const nodeIds = newNodes.map((n) => n.id);
    this.graph.setUndoBarrier();
    this.graph.edit(() => {
      const undoEntry = this.graph.addNodes(newNodes);
      undoEntry.add(this.groupsManagerView.createGroupFromNodes(nodeIds, groupName).entry);
      return undoEntry;
    });
    for (const id of nodeIds) {
      this.graph.updateNodeOutputs(id, false);
    }
    this.graph.disableUndoBarrier();
  }

  /** Shifts a set of nodes so their bounding-box top-left lands at `targetTopLeft` (positions only). */
  shiftNodesToTopLeft(nodes: Node[], targetTopLeft: { x: number; y: number }): Node[] {
    const preBounds = calculateMultiNodeBoundsWithPadding(nodes, DEFAULT_GROUP_PADDING);
    const dx = targetTopLeft.x - preBounds.x;
    const dy = targetTopLeft.y - preBounds.y;
    return nodes.map((n) => ({ ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }));
  }

  insertTemplateGraph(
    template: { name: string; data: SubflowTemplateData },
    targetTopLeft: { x: number; y: number },
  ): NodeId[] {
    const templateNodes = template.data.nodes;
    if (templateNodes.length === 0) {
      logger.warn('insertTemplateGraph called with empty template, skipping', { name: template.name });
      return [];
    }
    const templateEdges = template.data.edges ?? [];

    // Shift so the wrapper lands at targetTopLeft after duplication.
    const shiftedNodes = this.shiftNodesToTopLeft(templateNodes, targetTopLeft);

    let resultIds: NodeId[] = [];

    this.graph.edit(() => {
      const { undoEntry, newNodeIds } = this.graph.duplicateNodesAndEdges(shiftedNodes, templateEdges);

      const { entry: groupEntry, groupId } = this.groupsManagerView.createGroupFromNodes(
        newNodeIds,
        template.name,
        template.data.groupMetadata,
      );
      undoEntry.add(groupEntry);

      this.graph.setSelectedNodes([groupId]);
      resultIds = [groupId, ...newNodeIds];
      return undoEntry;
    });

    return resultIds;
  }

  /**
   * Inserts several preview templates in a SINGLE `ongoing` edit (one off-undo action covering all of them, so
   * {@link clearPreview} rolls back every staged template at once). Each template is centered on its
   * own `center`. Returns the new node ids per item (same order),
   * so callers can reveal/hide each template independently via {@link setNodesVisibility}.
   */
  insertPreviewTemplates(
    templateData: { data: SubflowTemplateData; center: { x: number; y: number } }[],
    options?: { ongoing?: boolean },
  ): NodeId[][] {
    const previewedTemplates: NodeId[][] = [];
    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();
      for (const {
        data: { nodes, edges },
        center,
      } of templateData) {
        if (nodes.length === 0) {
          previewedTemplates.push([]);
          continue;
        }
        const bounds = calculateMultiNodeBoundsWithPadding(nodes, DEFAULT_GROUP_PADDING);
        const targetTopLeft = { x: center.x - bounds.width / 2, y: center.y - bounds.height / 2 };
        const shiftedNodes = this.shiftNodesToTopLeft(nodes, targetTopLeft).map((n) => ({
          ...n,
          selected: false,
        }));
        const cleanEdges = (edges ?? []).map((e) => ({ ...e, selected: false }));
        const { undoEntry: entry, newNodeIds } = this.graph.duplicateNodesAndEdges(shiftedNodes, cleanEdges);
        undoEntry.add(entry);
        previewedTemplates.push(newNodeIds);
      }
      return undoEntry;
    }, options?.ongoing);
    return previewedTemplates;
  }

  /**
   * Rolls back the current preview (template-gallery hover or {@link previewGraph}) and repaints —
   * the rollback itself skips redraw. No-op if there's no ongoing preview edit.
   */
  clearPreview() {
    this.graph.cancelOngoingAction();
    this.redraw();
    void this.loadResources();
  }

  /**
   * Sets visibility of the given nodes and their internal edges in the React Flow layer — flips each
   * node's `hidden` (React Flow skips rendering hidden nodes) and applies the same flag to any edge whose
   * endpoints are both within `nodeIds`. Goes through the store only (no WASM, no undo entry), and skips
   * nodes/edges whose state is unchanged to avoid needless re-renders. Callers that need to split a graph
   * into a shown and a hidden subset should invoke this once per disjoint group with the right
   * `isVisible`; cross-group edges (one endpoint in either group) are intentionally not touched.
   */
  setNodesVisibility(nodeIds: NodeId[], isVisible: boolean) {
    const hidden = !isVisible;
    const nodeIdsSet = new Set(nodeIds);
    for (const id of nodeIds) {
      const node = this.graph.store.getNodeNullable(id);
      if (!node) continue;
      if (Boolean(node.hidden) === hidden) continue;
      this.graph.store.updateNode(id, () => ({ hidden }));
    }
    for (const edge of this.graph.store.getEdges()) {
      if (!nodeIdsSet.has(edge.source) || !nodeIdsSet.has(edge.target)) continue;
      if (Boolean(edge.hidden) === hidden) continue;
      this.graph.store.updateEdge(edge.id, () => ({ hidden }));
    }
  }

  /**
   * @deprecated Update node data through "View"s and FlowGraph instead.
   */
  updateNodeData<T extends Record<string, unknown>>(nodeId: NodeId, newData: Partial<T>) {
    this.graph.updateNodeData(nodeId, newData);
  }

  /**
   * Sets which of the node's collapsed handle-type groups are expanded, or writes `undefined` to
   * turn collapse-by-type mode off for this node entirely (as opposed to `{ expandedTypes: [] }`,
   * which leaves the mode on with every group collapsed).
   */
  setHandleCollapse(nodeId: NodeId, next: { expandedTypes: HandleType[] } | undefined): void {
    this.graph.edit(() => this.graph.updateNodeData<BaseNodeData>(nodeId, { handleCollapse: next }));
  }

  /**
   * Renames the node's display title via {@link Node.data.name}. When {@link Node.originalName} is still missing,
   * it is set from the previous title once (unsaved / legacy payloads); loaded recipes receive it from the API.
   */
  renameNodeDisplayName(nodeId: NodeId, newName: string) {
    this.graph.renameNodeDisplayName(nodeId, newName);
  }

  // TODO: Remove
  async loadResources() {
    await this.graph.loadResourcesAsync();
  }

  // TODO: Remove
  updateInputBeforeRun(
    modelNodeId: NodeId,
    nodeData: ModelBaseNodeData,
    cache: Map<string, ComputedImageEntry> = new Map(),
    sendComputedImageRefs = false,
    payloadNodeIds: ReadonlySet<string> = new Set(),
  ) {
    const parameterIds = this.graph.wasm.call((wasm) => wasm.parameterIds(modelNodeId)) ?? [];
    const params = Object.fromEntries(
      parameterIds.map((parameterId) => {
        const parameterValue = this.graph.wasm.call((wasm) => wasm.evaluateParameter(modelNodeId, parameterId));
        return [parameterId, parameterValue ? convertParameterValueToLegacyValue(parameterValue) : undefined];
      }),
    );
    const inputIds = this.graph.wasm.call((wasm) => wasm.inputIds(modelNodeId)) ?? [];
    const isLineageCaptureEnabled =
      posthog.isFeatureEnabled(FF_EXPORT_WORKFLOW_LINEAGE) === true ||
      (useUserStore.getState().user?.activeWorkspace?.subscription?.type === SubscriptionType.Enterprise &&
        posthog.isFeatureEnabled(FF_EXPORT_WORKFLOW_LINEAGE_ENTERPRISE) === true);
    const input = Object.fromEntries(
      inputIds.map((inputId) => {
        if (parameterIds.includes(inputId)) {
          return [inputId, params[inputId]];
        }

        const evaluatedInputValue = () => {
          const evaluateImageValue = (targetNodeId: NodeId, targetInputId: InputId) => {
            const input = this.graph.wasm.call((wasm) => wasm.nodeInput(targetNodeId, targetInputId));
            if (!input) {
              return undefined;
            }
            // Flag off keeps the inline snapshot even when the graphic will also execute.
            // Flag on: skip only when rust will fill this field (direct graphic, or nested helper).
            if (
              sendComputedImageRefs &&
              shouldSkipComputedImageSnapshot(input.nodeId, targetNodeId, modelNodeId, payloadNodeIds)
            ) {
              return undefined;
            }
            const snapshot = getOrCreateCached(cache, `${input.nodeId}:${input.outputId}`, () => {
              const img = this.graph.wasm.call((wasm) => wasm.exportNodeRender(input.nodeId, input.outputId));
              return img
                ? {
                    nodeId: input.nodeId,
                    outputId: input.outputId,
                    asset: { type: 'image' as const, url: img.data, width: img.width, height: img.height },
                  }
                : undefined;
            });
            if (!snapshot) {
              return undefined;
            }
            const { nodeId: sourceNodeId, outputId, asset } = snapshot;
            return sendComputedImageRefs ? { type: 'image_ref' as const, nodeId: sourceNodeId, outputId } : asset;
          };

          const inputValue = this.graph.evaluateInput(modelNodeId, inputId);

          if (!inputValue) return undefined;

          // If the input is a computed image, we need to render it
          if (inputValue.type === 'image' && inputValue.value.type === 'computed') {
            return evaluateImageValue(modelNodeId, inputId);
          }

          // When there is no video asset in the handle value, we return a placeholder object. This invokes
          // a server side computation of the video.
          // This is a temoprary fix until we refactor server side execution logic to support computed
          // assets more natively.
          if (inputValue.type === 'video' && inputValue.value.type === 'computed') {
            return {
              type: 'video_placeholder',
            };
          }

          if (inputValue.type === 'lora') {
            return `weavy-lora::${inputValue.value}`;
          }

          if (inputValue.type === 'kling_element') {
            const klingElementInput = this.graph.wasm.call((wasm) => wasm.nodeInput(modelNodeId, inputId));
            if (!klingElementInput) return undefined;

            const klingElementValue = inputValue.value;

            const resolveImage = (image: EvaluatedImage | undefined, inputId: string) => {
              if (image?.type === 'computed') {
                return evaluateImageValue(klingElementInput.nodeId, inputId);
              }
              return image;
            };

            return {
              type: 'kling_element',
              frontalImageUrl: resolveImage(klingElementValue.frontalImageUrl, 'frontal_image_url'),
              referenceImageUrl1: resolveImage(klingElementValue.referenceImageUrl1, 'reference_image_url1'),
              referenceImageUrl2: resolveImage(klingElementValue.referenceImageUrl2, 'reference_image_url2'),
              referenceImageUrl3: resolveImage(klingElementValue.referenceImageUrl3, 'reference_image_url3'),
            };
          }

          if (inputValue.type === 'keyframe') {
            const keyframeInput = this.graph.wasm.call((wasm) => wasm.nodeInput(modelNodeId, inputId));
            if (!keyframeInput) return undefined;

            const { imageUrl, timestampSeconds } = inputValue.value;
            const rendered =
              imageUrl?.type === 'computed' ? evaluateImageValue(keyframeInput.nodeId, 'image_url') : imageUrl;

            return {
              type: 'keyframe',
              // A computed image carries no url, so it has to be rendered. Keeping the unrendered one on
              // failure leaves the server a keyframe it can reject, rather than one it reads as empty.
              imageUrl: rendered ?? imageUrl,
              timestampSeconds,
            };
          }

          if (typeof inputValue.value === 'object' && !Array.isArray(inputValue.value)) {
            return { ...inputValue.value, type: inputValue.type };
          }
          return inputValue.value;
        };
        const stampLineageSource = (value: unknown): unknown => {
          if (!isLineageCaptureEnabled || value == null || typeof value !== 'object' || Array.isArray(value)) {
            return value;
          }
          if (typeof (value as { id?: unknown }).id === 'string') {
            return value;
          }
          return withLineageSourceAssetIds(value, resolveLineageSourceAssetIds(this.graph, modelNodeId, inputId));
        };

        return [inputId, stampLineageSource(evaluatedInputValue())];
      }),
    );
    nodeData.params = { ...nodeData.params, ...input, ...params };
    nodeData.input = { ...nodeData.input, ...input };
  }

  pasteAndPositionNodesAndEdges(
    clipboardNodes: Node[],
    clipboardEdges: Edge[],
    positionDuplicatedNodes: (nodes: Node[], newNodeIds: NodeId[]) => UndoRedoEntry,
    scrollNodeIntoViewIfNeeded: (nodes: Node[]) => void,
    options?: DuplicateNodeOptions,
  ) {
    this.graph.edit(() => {
      const { undoEntry, newNodeIds } = this.graph.duplicateNodesAndEdges(clipboardNodes, clipboardEdges, options);

      // Position the duplicated nodes
      undoEntry.add(positionDuplicatedNodes(clipboardNodes, newNodeIds));

      // Select the duplicated nodes and scroll them into view
      const newNodes = newNodeIds.map((id) => this.graph.store.getNode(id));
      this.selectedNodes = newNodeIds;
      scrollNodeIntoViewIfNeeded(newNodes);

      return undoEntry;
    });
  }

  duplicateNodes(
    nodeIds: NodeId[],
    positionDuplicatedNodes: (nodes: Node[], newNodeIds: NodeId[]) => UndoRedoEntry,
    scrollNodeIntoViewIfNeeded: (nodes: Node[]) => void,
    options?: DuplicateNodeOptions,
  ) {
    if (nodeIds.length === 0) return;
    const nodeIdsSet = new Set(nodeIds);

    const nodesIncludingChildren = nodeIds.flatMap((nodeId) => {
      const node = this.graph.store.getNode(nodeId);
      if (node.type === NodeType.CustomGroup) {
        return [node, ...this.getChildNodesOfGroup(node.id).filter((childNode) => !nodeIdsSet.has(childNode.id))];
      }
      return [node];
    });
    const nodesIncludingChildrenIdSet = new Set(nodesIncludingChildren.map((node) => node.id));

    const preserveExternalInputs = options?.preserveExternalInputs ?? false;
    const edgesToCopy = this.graph.store
      .getEdges()
      .filter((e) =>
        preserveExternalInputs
          ? nodesIncludingChildrenIdSet.has(e.target)
          : nodesIncludingChildrenIdSet.has(e.source) && nodesIncludingChildrenIdSet.has(e.target),
      );

    this.pasteAndPositionNodesAndEdges(
      nodesIncludingChildren,
      edgesToCopy,
      positionDuplicatedNodes,
      scrollNodeIntoViewIfNeeded,
      preserveExternalInputs ? { preserveExternalInputs: true } : undefined,
    );
    trackNodesAdded(nodesIncludingChildren, 'duplicate');
  }

  /**
   * Duplicate nodes in place (overlapping originals) for Option/Alt+drag. Selects the new nodes.
   * Callers typically pass `{ preserveExternalInputs: true }` so upstream edges into the selection are re-wired to the copies (same semantics as {@link duplicateNodes}).
   * Returns session data used by {@link handleNodeDrag} to move duplicates while keeping originals fixed.
   */
  beginDuplicateDrag(nodeIds: NodeId[], options?: DuplicateNodeOptions): DuplicateDragSession | null {
    if (nodeIds.length === 0) return null;

    const nodeIdsSet = new Set(nodeIds);
    const nodesIncludingChildren = nodeIds.flatMap((nodeId) => {
      const node = this.graph.store.getNode(nodeId);
      if (node.type === NodeType.CustomGroup) {
        return [node, ...this.getChildNodesOfGroup(node.id).filter((childNode) => !nodeIdsSet.has(childNode.id))];
      }
      return [node];
    });

    const frozenPositions = new Map<NodeId, { x: number; y: number }>();
    for (const n of nodesIncludingChildren) {
      if (this.graph.getEffectiveIsLocked(n.id)) {
        return null;
      }
      frozenPositions.set(n.id, { ...n.position });
    }

    const nodesIncludingChildrenIdSet = new Set(nodesIncludingChildren.map((node) => node.id));
    const preserveExternalInputs = options?.preserveExternalInputs ?? false;
    const edgesToCopy = this.graph.store
      .getEdges()
      .filter((e) =>
        preserveExternalInputs
          ? nodesIncludingChildrenIdSet.has(e.target)
          : nodesIncludingChildrenIdSet.has(e.source) && nodesIncludingChildrenIdSet.has(e.target),
      );

    this.graph.cancelOngoingAction();

    const { undoEntry, newNodeIds } = this.graph.duplicateNodesAndEdges(
      nodesIncludingChildren,
      edgesToCopy,
      preserveExternalInputs ? { preserveExternalInputs: true } : undefined,
    );

    const originalToNewId = new Map<NodeId, NodeId>();
    for (let i = 0; i < nodesIncludingChildren.length; i++) {
      const orig = nodesIncludingChildren[i];
      const newId = newNodeIds[i];
      if (orig && newId) {
        originalToNewId.set(orig.id, newId);
      }
    }
    this.selectedNodes = newNodeIds;

    trackNodesAdded(nodesIncludingChildren, 'duplicate');
    return { originalToNewId, frozenPositions, duplicateUndoEntry: undoEntry };
  }

  /** `measured` marks sizes the canvas measured, rather than a resize. */
  setNodeDimensions(
    dimensions: { nodeId: NodeId; width?: number; height?: number; updateStyle?: boolean; updateData?: boolean }[],
    { measured = false }: { measured?: boolean } = {},
  ): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();
    for (const dim of dimensions) {
      const { nodeId, width, height, updateData } = dim;
      const widthUpdate = 'width' in dim ? { width } : {};
      const heightUpdate = 'height' in dim ? { height } : {};
      const update = { ...widthUpdate, ...heightUpdate };
      if (Object.keys(update).length > 0) {
        undoEntry.add(
          this.graph.store.updateNode(
            nodeId,
            () => ({
              ...update,
            }),
            { measurementOnly: measured },
          ),
        );
        if (updateData) {
          // Writing the box size to `width`/`height` would corrupt the 3D viewport on these types.
          const nodeType = this.graph.store.getNodeNullable(nodeId)?.type;
          const dataUpdate =
            nodeType !== undefined && MEDIA_VIEWPORT_DIMENSION_NODE_TYPES.has(nodeType)
              ? {
                  ...('width' in dim ? { nodeWidth: width } : {}),
                  ...('height' in dim ? { nodeHeight: height } : {}),
                }
              : update;
          undoEntry.add(
            this.graph.store.updateNodeData<BaseNodeData & { height?: number; width?: number }>(
              nodeId,
              () => dataUpdate,
            ),
          );
        }
      }

      if (this.graph.isNodeInGroup(nodeId)) {
        undoEntry.add(this.groupsManagerView.expandGroupAndRebaseChildrenIfNeeded(this.getNode(nodeId)));
      }
    }
    return undoEntry;
  }

  resizeNode(
    nodeId: NodeId,
    dimensions: { width?: number; height?: number },
    ongoing: boolean,
    isDiagonalResizeEnabled: boolean,
  ): void {
    const node = this.graph.store.getNodeNullable(nodeId);
    // With the flag on `shouldApplyStoredBoxSize` passes on a missing type too, and the store
    // throws rather than no-ops for a node it does not hold.
    if (node === undefined) return;
    if (!shouldApplyStoredBoxSize(node.type, isDiagonalResizeEnabled)) return;
    this.graph.edit(() => this.setNodeDimensions([{ nodeId, ...dimensions, updateData: true }]), ongoing);
  }

  setNodePositions(positions: { nodeId: NodeId; position: { x: number; y: number } }[]): UndoRedoEntry {
    return this.graph.store.setNodePositions(positions);
  }

  editNodePositions(positions: { nodeId: NodeId; position: { x: number; y: number } }[], ongoing: boolean) {
    this.graph.cancelOngoingAction();
    if (
      !positions.every(({ nodeId, position }) => {
        const current = this.graph.store.getNode(nodeId).position;
        return (current.x - position.x) ** 2 + (current.y - position.y) ** 2 < 4;
      })
    ) {
      this.graph.edit(() => this.setNodePositions(positions), ongoing);
    }
  }

  /**
   * Move nodes and, in the same undo step, shrink-wrap the given groups around their (now moved)
   * children. Used by align/auto-arrange so a rearrange — positions plus any group resize — is a
   * single undo. Positions are applied first so the resize fits the new child positions.
   */
  editNodePositionsAndFitGroups(
    positions: { nodeId: NodeId; position: { x: number; y: number } }[],
    groupIdsToFit: NodeId[],
  ): void {
    this.graph.cancelOngoingAction();
    this.graph.edit(() => {
      const entry = this.setNodePositions(positions);
      entry.add(this.groupsManagerView.resizeGroupsToFitEntry(groupIdsToFit));
      return entry;
    }, false);
  }

  setNodePositionsAndDimensions(
    positions: {
      nodeId: NodeId;
      position: { x: number; y: number };
      width?: number;
      height?: number;
    }[],
  ) {
    const undoEntry = UndoRedoEntry.empty();
    for (const { nodeId, position, width, height } of positions) {
      undoEntry.add(this.setNodePositions([{ nodeId, position }]));
      if (width !== undefined && height !== undefined) {
        undoEntry.add(this.setNodeDimensions([{ nodeId, width, height }]));
      }
    }
    return undoEntry;
  }

  moveNodesBy(nodes: NodeId[], delta: { x: number; y: number }) {
    this.graph.edit(
      () =>
        this.setNodePositions(
          nodes.map((nodeId) => {
            const current = this.graph.store.getNode(nodeId).position;
            const newPosition = { x: current.x + delta.x, y: current.y + delta.y };
            return { nodeId, position: newPosition };
          }),
        ),
      false,
    );
  }

  getEffectiveIsLocked(nodeId: NodeId): boolean {
    return this.graph.getEffectiveIsLocked(nodeId);
  }

  toggleLockNode(nodeId: NodeId) {
    this.graph.edit(() => {
      return this.graph.toggleNodeLocked(nodeId);
    });
  }

  setNodesLockedState(nodeIds: NodeId[], locked: boolean) {
    this.graph.edit(() => this.graph.setNodesLockedState(nodeIds, locked));
  }

  getCanCompareInput(nodeId: NodeId): boolean {
    return (
      this.graph.wasm.call((wasm) => {
        return wasm.getCanCompareInput(nodeId);
      }) ?? false
    );
  }

  setCompareInput(nodeId: NodeId, showInput: boolean) {
    this.graph.wasm.call((wasm) => {
      wasm.setCompareInput(nodeId, showInput);
    });
  }

  getCanDownloadNode(nodeId: NodeId): boolean {
    return (
      this.graph.wasm.call((wasm) => {
        return wasm.getCanDownloadNode(nodeId);
      }) ?? false
    );
  }

  renderNodeImage(nodeId: NodeId, outputId?: OutputId, generationIndex?: number): string | undefined {
    const resolvedOutputId = outputId ?? this.getOutputIds(nodeId)[0];
    return this.graph.wasm.call((wasm) => wasm.exportNodeRender(nodeId, resolvedOutputId, undefined, generationIndex))
      ?.data;
  }

  /**
   * Filter out locked nodes and groups with locked children, update selection, track group deletes,
   * then remove the eligible nodes (optionally preserving edges across the gap).
   */
  deleteNodes(nodeIds: NodeId[], options?: DeleteNodesOptions): DeleteNodesResult {
    const result: DeleteNodesResult = {};
    const preserveBridging = options?.preserveBridgingConnections === true;

    this.graph.edit(() => {
      const { nodesToDelete, groupsWithLockedChildren } = nodeIds.reduce<{
        nodesToDelete: Node[];
        groupsWithLockedChildren: NodeId[];
      }>(
        (acc, nodeId) => {
          const node = this.graph.store.getNode(nodeId);
          if (this.graph.getEffectiveIsLocked(nodeId)) {
            return acc;
          }

          if (node.type !== NodeType.CustomGroup) {
            acc.nodesToDelete.push(node);
            return acc;
          }

          const groupChildren = this.graph.getChildNodesOfGroup(nodeId);
          if (groupChildren.some((child) => child.data.isLocked)) {
            acc.groupsWithLockedChildren.push(nodeId);
            return acc;
          } else {
            acc.nodesToDelete.push(node, ...groupChildren.filter((child) => !nodeIds.includes(child.id)));
            return acc;
          }
        },
        { nodesToDelete: [], groupsWithLockedChildren: [] },
      );

      if (groupsWithLockedChildren.length > 0) {
        result.preventedGroups = groupsWithLockedChildren;
      }

      const unlockedNodeIds = nodesToDelete.map((node) => node.id);
      this.selectedNodes = this.selectedNodes.filter((id) => !unlockedNodeIds.includes(id));
      const undoEntry = preserveBridging
        ? this.graph.removeNodesPreservingBridgingConnections(unlockedNodeIds)
        : this.graph.removeNodes(unlockedNodeIds);

      this.groupsManagerView.trackDeletedGroupNodes(nodesToDelete);

      if (!options?.alsoUndo) {
        return undoEntry;
      }
      // Side effect first, so undo restores the nodes and then runs it. A failure there
      // cannot skip the node restore. Redo still drops the side effect before deleting again.
      const combined = UndoRedoEntry.empty();
      combined.add(options.alsoUndo);
      combined.add(undoEntry);
      return combined;
    });

    return result;
  }

  deleteSelected(): DeleteNodesResult {
    if (this.selectedNodes.length > 0) {
      // deleting nodes deletes the relevant edges
      return this.deleteNodes(this.selectedNodes);
    } else if (this.selectedEdges.length > 0) {
      this.deleteEdges(this.selectedEdges);
      return {};
    }
    return {};
  }

  /**
   * See {@link FlowGraph.evaluateNode} for more information.
   */
  evaluateNode(nodeId: NodeId, index?: number): EvaluatedValue | undefined {
    return this.graph.evaluateNode(nodeId, index);
  }

  /**
   * See {@link FlowGraph.evaluateInput} for more information.
   */
  evaluateInput(nodeId: NodeId, inputId: InputId): EvaluatedValue | undefined {
    return this.graph.evaluateInput(nodeId, inputId);
  }

  /**
   * Poster image for a node's image output ({@link resolveNodePreviewImagePoster}). Prefer calling
   * from user actions; computed images trigger an expensive WASM export.
   */
  resolveNodePreviewImagePoster(nodeId: NodeId): ImageRef | undefined {
    return resolveNodePreviewImagePoster(this.graph, nodeId);
  }

  /**
   * See {@link FlowGraph.evaluateOutput} for more information.
   */
  evaluateOutput(input: Input): EvaluatedValue | undefined {
    return this.graph.evaluateOutput(input);
  }

  listSelectorOptions(nodeId: NodeId): string[] {
    const value = this.evaluateNode(nodeId);
    return value?.type === 'string_array' ? value.value : [];
  }

  /**
   * See {@link FlowGraph.isInputValid} for more information.
   */
  isInputValid(nodeId: NodeId, inputId: InputId): boolean {
    return this.graph.isInputValid(nodeId, inputId);
  }

  /**
   * See {@link FlowGraph.nodeValidInputTypes} for more information.
   */
  nodeValidInputTypes(nodeId: NodeId, inputId: InputId): Array<HandleType> {
    return this.graph.nodeValidInputTypes(nodeId, inputId);
  }

  /**
   * See {@link FlowGraph.nodeInputType} for more information.
   */
  nodeInputType(nodeId: NodeId, inputId: InputId): HandleType | undefined {
    return this.graph.nodeInputType(nodeId, inputId);
  }

  /**
   * See {@link FlowGraph.nodeOutputType} for more information.
   */
  nodeOutputType(nodeId: NodeId, outputId: OutputId): HandleType | undefined {
    return this.graph.nodeOutputType(nodeId, outputId);
  }

  /**
   * The declared type of a node's first output — a schema lookup, not a graph evaluation, so it is
   * safe to call while rendering. Undefined for a node that declares no output, and for one WASM
   * has no node for at all: a sticky note or a group lives only in the store, and `outputIds`
   * throws rather than returning nothing for an id it does not know.
   */
  nodePrimaryOutputType(nodeId: NodeId): HandleType | undefined {
    try {
      const outputId = this.getOutputIds(nodeId)[0];
      return outputId === undefined ? undefined : this.nodeOutputType(nodeId, outputId);
    } catch (e) {
      if (!warnedOutputTypeLookups.has(nodeId)) {
        warnedOutputTypeLookups.add(nodeId);
        logger.warn(`Cannot resolve the output type of node ${nodeId}`, e);
      }
      return undefined;
    }
  }

  /**
   * Returns the display name for an input handle. For compositor nodes, the
   * name is derived from the corresponding layer's name (the authoritative
   * source) rather than the stored handle label.
   */
  getInputHandleName(nodeId: string, handleKey: string): string | undefined {
    const node = this.graph.store.getNodeNullable(nodeId);
    if (node?.type === 'compv3') {
      const compositor = (node.data as CompositorNodeV3).data;
      const layer = Object.values(compositor.layers).find((l) => layerInputId(compositor, l) === handleKey);
      if (layer) return layer.name;
      return undefined;
    }
    return undefined;
  }

  /**
   * See {@link FlowGraph.getOutputIds} for more information.
   */
  getOutputIds(nodeId: NodeId): OutputId[] {
    return this.graph.getOutputIds(nodeId);
  }

  isGroupNode(nodeId: NodeId): boolean {
    return this.graph.store.getNode(nodeId).type === NodeType.CustomGroup;
  }

  getChildNodesOfGroup(groupId: NodeId): Node[] {
    return this.graph.getChildNodesOfGroup(groupId);
  }

  /// EDGES

  getEdge(edgeId: EdgeId): Edge {
    return this.graph.store.getEdge(edgeId);
  }

  getEdgeNullable(edgeId: EdgeId): Edge | undefined {
    return this.graph.store.getEdgeNullable(edgeId);
  }

  wouldConnectionResultInCycle(source: NodeId, target: NodeId): boolean {
    return this.graph.wouldConnectionResultInCycle(source, target);
  }

  connectEdge(source: NodeId, target: NodeId, sourceHandle: string, targetHandle: string) {
    this.graph.edit(() => {
      return this.connectEdgeToTarget(source, target, sourceHandle, targetHandle);
    });
  }

  reconnectEdge(source: NodeId, target: NodeId, sourceHandle: string, targetHandle: string, oldEdge: Edge) {
    if (
      source === oldEdge.source &&
      sourceHandle === oldEdge.sourceHandle &&
      target === oldEdge.target &&
      targetHandle === oldEdge.targetHandle
    ) {
      // reconnecting edge to same handle, do nothing
      return;
    }
    this.graph.edit(() => {
      const undoEntry = this.graph.removeEdges([oldEdge.id]);
      undoEntry.add(this.connectEdgeToTarget(source, target, sourceHandle, targetHandle));
      return undoEntry;
    });
  }

  /**
   * Handles continuous drag operations and drag stop operations.
   * Each node is processed individually based on its own characteristics.
   * Updates positions. For drag stop, it also performs group attachment and removal from groups.
   */
  handleNodeDrag(
    nodes: Node[],
    isGroupModifierPressed: boolean,
    isOngoing: boolean = true,
    duplicateDragSession?: DuplicateDragSession | null,
  ): void {
    // Async paste initialization can remove a node before React Flow finishes its drag.
    nodes = nodes.filter((node) => this.graph.store.getNodeNullable(node.id));
    if (nodes.length === 0) return;

    const effectiveGroupModifier = duplicateDragSession ? false : isGroupModifierPressed;

    const nodesForLogic =
      duplicateDragSession !== undefined && duplicateDragSession !== null
        ? nodes.flatMap((n) => {
            const newId = duplicateDragSession.originalToNewId.get(n.id);
            if (!newId) return [];
            const base = this.graph.store.getNode(newId);
            return [{ ...base, position: n.position }];
          })
        : nodes;

    const buildDragUndoEntry = (): UndoRedoEntry => {
      const undoEntry = UndoRedoEntry.empty();

      // On drag end, fold the duplicate into the same undo step as the move (see pasteAndPositionNodesAndEdges).
      if (duplicateDragSession?.duplicateUndoEntry && !isOngoing) {
        undoEntry.add(duplicateDragSession.duplicateUndoEntry);
      }

      if (duplicateDragSession?.frozenPositions) {
        for (const n of nodes) {
          const frozen = duplicateDragSession.frozenPositions.get(n.id);
          if (frozen) {
            undoEntry.add(this.setNodePositions([{ nodeId: n.id, position: frozen }]));
          }
        }
      }

      const { groupNodes, groupedNodes, ungroupedNodes } = categorizeNodesGroupStatus(nodesForLogic);
      const draggedGroupIds = new Set(groupNodes.map((node) => node.id));

      // Update positions for group nodes and ungrouped nodes (simple position updates)
      for (const node of [...groupNodes, ...ungroupedNodes]) {
        undoEntry.add(this.setNodePositions([{ nodeId: node.id, position: node.position }]));
      }

      // Handle grouped nodes specially (may expand group or update position)
      undoEntry.add(this.handleGroupedNodesDrag(groupedNodes, draggedGroupIds, effectiveGroupModifier));

      // Handle drag stop (attachment/detachment). Not on the read-only Tool tab, where a drag only
      // moves: joining or leaving a group would change the node's lock, and so the tool's inputs.
      if (!isOngoing && !isCanvasReadOnlyForToolTab()) {
        undoEntry.add(
          this.handleDragStop(groupedNodes, groupNodes, ungroupedNodes, draggedGroupIds, effectiveGroupModifier),
        );
      }

      return undoEntry;
    };

    this.graph.edit(buildDragUndoEntry, isOngoing);
  }

  /**
   * Handles drag operations for nodes that are inside groups.
   * - If modifier is pressed: updates position (preparing to detach on drag stop)
   * - If dragged without its group: expand/rebase group bounds so the frame and relative coords stay correct
   * - Otherwise: position is handled by parent group drag
   */
  private handleGroupedNodesDrag(
    groupedNodes: Node[],
    draggedGroupIds: Set<string>,
    isGroupModifierPressed: boolean,
  ): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();

    for (const node of groupedNodes) {
      const isDraggedWithoutItsGroup = !draggedGroupIds.has(node.parentId!);

      if (isGroupModifierPressed) {
        // Modifier pressed: update position (will be detached on drag stop)
        undoEntry.add(this.setNodePositions([{ nodeId: node.id, position: node.position }]));
      } else if (isDraggedWithoutItsGroup) {
        // Dragged without parent group: expand the group to accommodate
        undoEntry.add(this.groupsManagerView.expandGroupAndRebaseChildrenIfNeeded(node, true));
      }
      // Else: node is dragged with its parent group, position handled by parent
    }

    return undoEntry;
  }

  /**
   * Handles node attachment/detachment when drag operation completes.
   * - If modifier is pressed: detach grouped nodes from their groups
   * - Otherwise: attach nodes to groups they intersect with
   */
  private handleDragStop(
    groupedNodes: Node[],
    groupNodes: Node[],
    ungroupedNodes: Node[],
    draggedGroupIds: Set<string>,
    isGroupModifierPressed: boolean,
  ): UndoRedoEntry {
    const undoEntry = UndoRedoEntry.empty();

    if (isGroupModifierPressed) {
      undoEntry.add(this.detachNodesFromGroups(groupedNodes, draggedGroupIds));
    } else {
      const groupAttachments = this.findUngroupedNodesIntersectingWithGroups(groupNodes);
      const nodeGroupId = this.findGroupIntersectingWithNodes(ungroupedNodes);

      for (const { groupId, nodes: nodesToAttach } of groupAttachments) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup(nodesToAttach, groupId));
      }

      if (nodeGroupId) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup(ungroupedNodes, nodeGroupId));
      }
    }

    return undoEntry;
  }

  private detachNodesFromGroups(groupedNodes: Node[], draggedGroupIds: Set<string>): UndoRedoEntry {
    const nodesToRemove = groupedNodes.filter((node) => !node.parentId || !draggedGroupIds.has(node.parentId));
    return nodesToRemove.length > 0
      ? this.groupsManagerView.removeNodesFromGroup(nodesToRemove)
      : UndoRedoEntry.empty();
  }

  isNodeInGroup(node: Node): boolean {
    return !!node.parentId;
  }

  removeNodesFromGroup(NodeIds: NodeId[]) {
    this.graph.edit(() => {
      const nodes = NodeIds.map((nodeId) => this.graph.store.getNode(nodeId));
      return this.groupsManagerView.removeNodesFromGroup(nodes);
    });
  }

  getParentGroup(nodeId: NodeId): string | undefined {
    return this.graph.store.getNodeNullable(nodeId)?.parentId;
  }

  /**
   * Pure query method: Gets the group ID that intersects with ungrouped nodes
   * Used for highlighting during UNGROUPED drag state
   */
  getIntersectingGroupForNodes(nodes: Node[]): string | null {
    return this.groupsManagerView.findIntersectingGroup(nodes);
  }

  /**
   * Finds which stationary nodes each group currently intersects with.
   * Returns an array where each entry represents a group and the nodes that partially intersects with it.
   *
   * @param groupNodes - Array of group nodes
   */
  findUngroupedNodesIntersectingWithGroups(groupNodes: Node[]): Array<{ groupId: string; nodes: Node[] }> {
    return this.groupsManagerView.findUngroupedNodesIntersectingWithGroups(groupNodes);
  }

  /**
   * Finds which group (if any) the given nodes intersects with.
   * Returns the group ID or null if no suitable group is found.
   *
   * @param nodes - Array of nodes
   */
  findGroupIntersectingWithNodes(nodes: Node[]): string | null {
    return this.groupsManagerView.findIntersectingGroup(nodes);
  }

  attachNodesToGroup(nodes: Node[], groupId: string): UndoRedoEntry {
    return this.groupsManagerView.attachNodesToGroup(nodes, groupId);
  }

  /**
   * Inserts a Router node on an output handle. The router's input is connected
   * to the source output, and all edges that were connected to that output
   * are rewired to come from the router's output instead.
   */
  insertRouterOnOutput(sourceNodeId: NodeId, sourceHandleId: string) {
    const sourceNode = this.graph.store.getNodeNullable(sourceNodeId);
    if (!sourceNode) {
      return;
    }

    const nodeTypes = useWorkflowStore.getState().nodeTypes;
    const routerDefinition = nodeTypes.find((n) => n.type === 'router');
    if (!routerDefinition) {
      return;
    }

    const ROUTER_OFFSET_X = 120;
    const ROUTER_OFFSET_Y = 60;

    // Child nodes in a group store positions relative to the group. attachNodesToGroup and
    // calculateExpandedGroupBounds expect absolute flow coordinates before conversion.
    let position: { x: number; y: number };
    if (sourceNode.parentId) {
      const parentGroup = this.graph.store.getNodeNullable(sourceNode.parentId);
      if (parentGroup) {
        const sourceAbsolute = convertChildPositionToAbsolute(sourceNode, parentGroup.position).position;
        position = {
          x: sourceAbsolute.x + (sourceNode.width ?? DEFAULT_NODE_WIDTH) + ROUTER_OFFSET_X,
          y: sourceAbsolute.y + ROUTER_OFFSET_Y,
        };
      } else {
        position = {
          x: sourceNode.position.x + (sourceNode.width ?? DEFAULT_NODE_WIDTH) + ROUTER_OFFSET_X,
          y: sourceNode.position.y + ROUTER_OFFSET_Y,
        };
      }
    } else {
      position = {
        x: sourceNode.position.x + (sourceNode.width ?? DEFAULT_NODE_WIDTH) + ROUTER_OFFSET_X,
        y: sourceNode.position.y + ROUTER_OFFSET_Y,
      };
    }

    const routerNode = createNewNode(routerDefinition.id, nodeTypes, position);
    if (!routerNode) {
      return logger.warn('Failed to create router node');
    }

    routerNode.data = mergeRouterNodeDataForInsert(routerNode.data);

    const routerNodeId = routerNode.id;
    const outgoingEdges = this.graph.store.getEdges().filter((edge) => edge.sourceHandle === sourceHandleId);

    this.graph.edit(() => {
      const undoEntry = this.graph.addNodes([routerNode]);

      // Same group as source: use attachNodesToGroup so parentId, WASM parent, relative position, and group bounds stay in sync.
      if (sourceNode.parentId) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup([routerNode], sourceNode.parentId));
      } else {
        const intersectingGroupId = this.groupsManagerView.findIntersectingGroup([routerNode]);
        if (intersectingGroupId) {
          undoEntry.add(this.groupsManagerView.attachNodesToGroup([routerNode], intersectingGroupId));
        }
      }

      const routerInputKey = this.graph.getInputIds(routerNodeId)[0];
      const routerOutputKey = this.graph.getOutputIds(routerNodeId)[0];
      if (routerInputKey === undefined || routerOutputKey === undefined) {
        return undoEntry;
      }

      const routerInputHandle = getHandleId(routerNodeId, 'input', routerInputKey);
      undoEntry.add(this.connectEdgeToTarget(sourceNodeId, routerNodeId, sourceHandleId, routerInputHandle));

      const routerOutputHandle = getHandleId(routerNodeId, 'output', routerOutputKey);
      for (const edge of outgoingEdges) {
        undoEntry.add(this.graph.removeEdges([edge.id]));
        const newEdge = this.graph.createEdge(routerNodeId, edge.target, routerOutputHandle, edge.targetHandle!);
        undoEntry.add(this.graph.addEdges([newEdge]));
      }

      return undoEntry;
    });

    this.graph.updateNodeOutputs(routerNodeId, false);
    this.selectedNodes = [routerNodeId];
    trackNodesAdded([routerNode], 'handle_double_click');
  }

  replaceNode(nodeId: NodeId, nodeDefinitionId: string): Node | undefined {
    const node = this.graph.store.getNodeNullable(nodeId);
    if (!node) return undefined;

    const parentGroup = node.parentId ? this.graph.store.getNodeNullable(node.parentId) : undefined;
    const position = parentGroup ? convertChildPositionToAbsolute(node, parentGroup.position).position : node.position;
    const newNode = createNewNode(nodeDefinitionId, useWorkflowStore.getState().nodeTypes, position);
    if (!newNode) return undefined;

    this.graph.edit(() => {
      const undoEntry = this.graph.removeNodes([nodeId]);
      undoEntry.add(this.graph.addNodes([newNode]));
      if (parentGroup) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup([newNode], parentGroup.id));
      }
      return undoEntry;
    });

    this.graph.updateNodeOutputs(newNode.id, false);
    this.selectedNodes = [newNode.id];
    return newNode;
  }

  /**
   * Inserts a Prompt node upstream of a target input handle. The prompt's output is
   * connected to the target input. Caller is expected to only invoke this when the
   * target handle is currently unconnected.
   */
  insertPromptOnInput(targetNodeId: NodeId, targetHandleId: string) {
    const targetNode = this.graph.store.getNodeNullable(targetNodeId);
    if (!targetNode) return;

    const nodeTypes = useWorkflowStore.getState().nodeTypes;

    const parentGroup = targetNode.parentId ? this.graph.store.getNodeNullable(targetNode.parentId) : null;
    const basePosition = parentGroup
      ? convertChildPositionToAbsolute(targetNode, parentGroup.position).position
      : targetNode.position;
    const position = {
      x: basePosition.x - PROMPT_OFFSET_X,
      y: basePosition.y + PROMPT_OFFSET_Y,
    };

    const promptNode = createNewNode(PROMPT_NODE_ID, nodeTypes, position);
    if (!promptNode) {
      return logger.warn('Failed to create prompt node');
    }

    const promptNodeId = promptNode.id;

    this.graph.edit(() => {
      const undoEntry = this.graph.addNodes([promptNode]);

      if (targetNode.parentId) {
        undoEntry.add(this.groupsManagerView.attachNodesToGroup([promptNode], targetNode.parentId));
      } else {
        const intersectingGroupId = this.groupsManagerView.findIntersectingGroup([promptNode]);
        if (intersectingGroupId) {
          undoEntry.add(this.groupsManagerView.attachNodesToGroup([promptNode], intersectingGroupId));
        }
      }

      const promptOutputKey = this.graph.getOutputIds(promptNodeId)[0];
      if (promptOutputKey === undefined) {
        return undoEntry;
      }
      const promptOutputHandle = getHandleId(promptNodeId, 'output', promptOutputKey);
      if (!promptOutputHandle) {
        return undoEntry;
      }

      undoEntry.add(this.connectEdgeToTarget(promptNodeId, targetNodeId, promptOutputHandle, targetHandleId));
      return undoEntry;
    });

    this.graph.updateNodeOutputs(promptNodeId, false);
    this.selectedNodes = [promptNodeId];
    usePromptEditorFocusStore.getState().setPendingFocusNodeId(promptNodeId);
    trackNodesAdded([promptNode], 'handle_double_click');
  }

  // Callers pass edges in output→input order, so `target` is always the input node here.
  isAudioConnectionRejected(source: NodeId, sourceHandle: string, target: NodeId): boolean {
    return (
      this.graph.rejectsAudioInput(target) && this.graph.outputTypeForHandle(source, sourceHandle) === HandleType.Audio
    );
  }

  private connectEdgeToTarget(
    source: NodeId,
    target: NodeId,
    sourceHandle: string,
    targetHandle: string,
  ): UndoRedoEntry {
    if (this.isAudioConnectionRejected(source, sourceHandle, target)) {
      return UndoRedoEntry.empty();
    }
    const newEdge = this.graph.createEdge(source, target, sourceHandle, targetHandle);
    const existingEdge = this.graph.store
      .getEdges()
      .find((edge) => edge.target === newEdge.target && edge.targetHandle === newEdge.targetHandle);
    let undoEntry: UndoRedoEntry;
    if (existingEdge) {
      undoEntry = UndoRedoEntry.empty();
      undoEntry.add(this.graph.removeEdges([existingEdge.id]));
      undoEntry.add(this.graph.addEdges([newEdge]));
    } else {
      undoEntry = this.graph.addEdges([newEdge]);
    }

    requestFigmaNodeLayerPickerForTargetInput({
      graph: this.graph,
      onAutoOpen: () => {
        this.selectedNodes = [target];
      },
      targetNodeId: target,
      targetHandleId: targetHandle,
    });

    return undoEntry;
  }

  getEdges(): Edge[] {
    return this.graph.store.getEdges();
  }

  deleteEdges(edgeIds: EdgeId[], options?: Pick<DeleteNodesOptions, 'alsoUndo'>) {
    this.graph.edit(() => {
      const undoEntry = this.graph.removeEdges(edgeIds);
      if (!options?.alsoUndo) {
        return undoEntry;
      }
      // Same order as deleteNodes: undo restores the wires, then the tool outputs.
      const combined = UndoRedoEntry.empty();
      combined.add(options.alsoUndo);
      combined.add(undoEntry);
      return combined;
    });
  }

  /// UNDO REDO
  undo() {
    this.graph.undo();
  }

  redo() {
    this.graph.redo();
  }

  hasUndo() {
    return this.graph.hasUndo();
  }

  hasRedo() {
    return this.graph.hasRedo();
  }

  /// SELECTION
  get selectedNodes(): NodeId[] {
    return this.graph.getSelectedNodes();
  }

  /** Returns a snapshot of node IDs in selection order. */
  get selectedNodesInSelectionOrder(): NodeId[] {
    return this.graph.getSelectedNodesInSelectionOrder();
  }

  set selectedNodes(nodeIds: NodeId[]) {
    this.graph.setSelectedNodes(nodeIds);
  }

  get selectedEdges(): EdgeId[] {
    return this.graph.getSelectedEdges();
  }

  /** Edge ids explicitly chosen in React Flow (excludes incident-only highlights). */
  get explicitSelectedEdges(): EdgeId[] {
    return this.graph.getExplicitSelectedEdgeIds();
  }

  set selectedEdges(edgeIds: EdgeId[]) {
    this.graph.setSelectedEdges(edgeIds);
  }

  isNodeSelected(nodeId: NodeId): boolean {
    const node = this.graph.store.getNodeNullable(nodeId);

    if (!node) {
      return false;
    }

    return node.selected ?? false;
  }

  /// Validation
  missingRequiredInputs(nodeId: NodeId): string[] {
    return (
      this.graph.wasm.call((wasm) => {
        return wasm.inputIds(nodeId).filter((inputId) => {
          return wasm.isInputRequired(nodeId, inputId) && wasm.nodeInput(nodeId, inputId) === undefined;
        });
      }) ?? []
    );
  }

  invalidRequiredInputs(nodeId: NodeId): string[] {
    return (
      this.graph.wasm.call((wasm) => {
        return wasm.inputIds(nodeId).filter((inputId) => {
          return wasm.isInputRequired(nodeId, inputId) && !wasm.isInputValid(nodeId, inputId);
        });
      }) ?? []
    );
  }

  /**
   * Drops an edge from a source output onto a target node's body:
   *   1. Tries an existing empty input handle of matching type.
   *   2. Otherwise falls through to dynamic-input / compositor-layer / prompt-variable cascade.
   */
  tryAutoConnectOnDrop(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): boolean {
    if (!this.canAutoConnectOnDrop(sourceNodeId, targetNodeId, connectionHandleType)) return false;
    let entry: UndoRedoEntry | null = null;
    this.graph.edit(() => {
      entry = this.autoConnectOnDropEntry(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType);
      return entry ?? UndoRedoEntry.empty();
    });
    return entry !== null;
  }

  /** Target-side mirror of {@link tryAutoConnectOnDrop}; outputs are static, no cascade. */
  tryAutoConnectOnDropFromTarget(
    targetNodeId: NodeId,
    targetHandleId: string,
    sourceNodeId: NodeId,
    connectionHandleType: HandleType,
  ): boolean {
    if (!this.canAutoConnectOnDropFromTarget(targetNodeId, sourceNodeId, connectionHandleType)) return false;
    let entry: UndoRedoEntry | null = null;
    this.graph.edit(() => {
      entry = this.autoConnectOnDropFromTargetEntry(targetNodeId, targetHandleId, sourceNodeId, connectionHandleType);
      return entry ?? UndoRedoEntry.empty();
    });
    return entry !== null;
  }

  private canAutoConnectOnDrop(sourceNodeId: NodeId, targetNodeId: NodeId, connectionHandleType: HandleType): boolean {
    if (sourceNodeId === targetNodeId) return false;
    if (this.graph.wouldConnectionResultInCycle(sourceNodeId, targetNodeId)) return false;
    return (
      this.findFirstEmptyCompatibleInput(targetNodeId, connectionHandleType) !== null ||
      this.canAddDynamicInput(targetNodeId, connectionHandleType) ||
      this.canAddVideoConcatInput(targetNodeId, connectionHandleType) ||
      this.canAddCompositorLayer(targetNodeId, connectionHandleType) ||
      this.canAddPromptVariable(targetNodeId, connectionHandleType) ||
      this.canAddFigmaVariableInput(targetNodeId, connectionHandleType)
    );
  }

  private canAutoConnectOnDropFromTarget(
    targetNodeId: NodeId,
    sourceNodeId: NodeId,
    connectionHandleType: HandleType,
  ): boolean {
    if (sourceNodeId === targetNodeId) return false;
    if (this.graph.wouldConnectionResultInCycle(sourceNodeId, targetNodeId)) return false;
    return this.findFirstCompatibleOutput(sourceNodeId, connectionHandleType) !== null;
  }

  /** Input capability, independent of current count or whether the user can edit. */
  canAddInputHandles(nodeId: NodeId): boolean {
    const node = this.graph.store.getNodeNullable(nodeId);
    if (!node) return false;
    const modelName = getModelIdentifier(node.data as ModelBaseNodeData);
    return Boolean(
      modelName &&
      (LORA_INPUT_MODELS.has(modelName) ||
        KLING_V4_MULTI_PROMPT_MODELS.has(modelName) ||
        new ModelBaseView(this.graph, nodeId).getDynamicInputConfigs(modelName).length > 0),
    );
  }

  private canAddDynamicInput(targetNodeId: NodeId, connectionHandleType: HandleType): boolean {
    const targetData = this.graph.store.getNodeDataNullable<ModelBaseNodeData>(targetNodeId);
    if (!targetData) return false;
    const modelName = getModelIdentifier(targetData);
    if (!modelName) return false;
    const modelBaseView = new ModelBaseView(this.graph, targetNodeId);
    const config = findDynamicInputConfig(modelBaseView.getDynamicInputConfigs(modelName), connectionHandleType);
    if (!config) return false;
    return !getModelInputLimitStatus(modelName, targetData.handles.input, config.prefix).reachedLimit;
  }

  private canAddCompositorLayer(targetNodeId: NodeId, connectionHandleType: HandleType): boolean {
    const targetNode = this.graph.store.getNodeNullable(targetNodeId);
    if (!targetNode || targetNode.type !== 'compv3') return false;
    const inputIds = this.graph.getInputIds(targetNodeId);
    if (inputIds.length === 0) return false;
    return this.graph.nodeValidInputTypes(targetNodeId, inputIds[0]).includes(connectionHandleType);
  }

  private canAddVideoConcatInput(targetNodeId: NodeId, connectionHandleType: HandleType): boolean {
    return (
      this.graph.store.getNodeNullable(targetNodeId)?.type === 'video_concatenator' &&
      (connectionHandleType === HandleType.Video || connectionHandleType === HandleType.Any)
    );
  }

  private canAddPromptVariable(targetNodeId: NodeId, connectionHandleType: HandleType): boolean {
    if (connectionHandleType !== HandleType.Text) return false;
    const targetNode = this.graph.store.getNodeNullable(targetNodeId);
    if (!targetNode) return false;
    // A new variable rewires the prompt, which a lock blocks even where the Tool tab lets you type in it.
    if (this.graph.getEffectiveIsLocked(targetNodeId)) return false;
    return targetNode.type === NodeType.PromptV3 || targetNode.type === NodeType.Prompt;
  }

  private canAddFigmaVariableInput(
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): connectionHandleType is FigmaNodeHandleType {
    if (!this.isFigmaVariableHandleType(connectionHandleType)) return false;
    const targetNode = this.graph.store.getNodeNullable(targetNodeId);
    return targetNode?.type === NodeType.FigmaNode;
  }

  private isFigmaVariableHandleType(handleType: HandleType): handleType is FigmaNodeHandleType {
    return handleType === HandleType.Image || handleType === HandleType.Text;
  }

  /** Must be called inside `graph.edit`. Returns the undo entry, or null if no connection made. */
  private autoConnectOnDropEntry(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): UndoRedoEntry | null {
    if (sourceNodeId === targetNodeId) return null;
    if (this.graph.wouldConnectionResultInCycle(sourceNodeId, targetNodeId)) return null;
    if (connectionHandleType === HandleType.Audio && this.graph.rejectsAudioInput(targetNodeId)) return null;

    const emptyInputKey = this.findFirstEmptyCompatibleInput(targetNodeId, connectionHandleType);
    if (emptyInputKey) {
      const targetHandle = getHandleId(targetNodeId, 'input', emptyInputKey);
      const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandle);
      const undoEntry = this.graph.addEdges([newEdge]);
      requestFigmaNodeLayerPickerForTargetInput({
        graph: this.graph,
        onAutoOpen: () => {
          this.selectedNodes = [targetNodeId];
        },
        targetNodeId,
        targetHandleId: targetHandle,
      });
      return undoEntry;
    }

    return (
      this.addDynamicInputEntry(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType) ??
      this.addVideoConcatInputEntry(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType) ??
      this.addCompositorLayerEntry(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType) ??
      this.addPromptVariableEntry(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType) ??
      this.addFigmaVariableInputEntry(sourceNodeId, sourceHandleId, targetNodeId, connectionHandleType)
    );
  }

  /** Must be called inside `graph.edit`. Returns the undo entry, or null if no connection made. */
  private autoConnectOnDropFromTargetEntry(
    targetNodeId: NodeId,
    targetHandleId: string,
    sourceNodeId: NodeId,
    connectionHandleType: HandleType,
  ): UndoRedoEntry | null {
    if (sourceNodeId === targetNodeId) return null;
    if (this.graph.wouldConnectionResultInCycle(sourceNodeId, targetNodeId)) return null;

    const matchedOutputKey = this.findFirstCompatibleOutput(sourceNodeId, connectionHandleType);
    if (!matchedOutputKey) return null;

    const sourceHandleId = getHandleId(sourceNodeId, 'output', matchedOutputKey);
    return this.connectEdgeToTarget(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
  }

  /** Candidates with no compatible handle on the primary's side are silently skipped. */
  findSiblingConnections(primary: Connection, candidateNodeIds: NodeId[]): Connection[] {
    if (!primary.nodeId || !primary.handleType || !primary.handleSide) return [];
    const siblings: Connection[] = [];
    for (const candidateId of candidateNodeIds) {
      if (candidateId === primary.nodeId) continue;

      let matchedKey: string | undefined;
      if (primary.handleSide === 'source') {
        for (const outputId of this.graph.getOutputIds(candidateId)) {
          if (this.isOutputCompatible(candidateId, outputId, primary.handleType)) {
            matchedKey = outputId;
            break;
          }
        }
      } else {
        for (const inputId of this.graph.getInputIds(candidateId)) {
          if (this.isAutomaticInputCompatible(candidateId, inputId, primary.handleType)) {
            matchedKey = inputId;
            break;
          }
        }
      }

      if (matchedKey === undefined) continue;
      const side = primary.handleSide === 'source' ? 'output' : 'input';
      // Use the sibling's own type, not primary's: when primary is Any, every sibling would
      // otherwise be mislabeled as Any and fail downstream type-specific compatibility checks.
      const siblingType =
        primary.handleSide === 'source'
          ? (this.graph.nodeOutputType(candidateId, matchedKey) ?? primary.handleType)
          : (this.graph.nodeInputType(candidateId, matchedKey) ?? primary.handleType);
      siblings.push({
        nodeId: candidateId,
        handleId: getHandleId(candidateId, side, matchedKey),
        handleSide: primary.handleSide,
        handleType: siblingType,
      });
    }
    return siblings;
  }

  /** Primary cascades first; if it fails, no sibling edges are created. One undo step. */
  connectAllSourcesToTarget(
    primary: Connection,
    additional: Connection[],
    target: { nodeId: NodeId; handleId?: string },
  ): ConnectResult {
    if (!primary.nodeId || !primary.handleId || !primary.handleType || !primary.handleSide) {
      return { primary: 'no-slot', siblings: [] };
    }
    const primaryStatus = this.dryRunOneSource(primary, target);
    if (primaryStatus !== 'ok') {
      return { primary: primaryStatus, siblings: [] };
    }
    let result: ConnectResult = { primary: 'no-slot', siblings: [] };
    this.graph.edit(() => {
      const undoEntry = UndoRedoEntry.empty();
      const primaryResult = this.connectOneSourceEntry(primary, target);
      if (primaryResult.entry) undoEntry.add(primaryResult.entry);
      if (primaryResult.status !== 'ok') {
        result = { primary: primaryResult.status, siblings: [] };
        return undoEntry;
      }
      const siblings = additional.map((c) => {
        const r = this.connectOneSourceEntry(c, { nodeId: target.nodeId });
        if (r.entry) undoEntry.add(r.entry);
        return { sourceNodeId: c.nodeId ?? '', status: r.status };
      });
      result = { primary: 'ok', siblings };
      return undoEntry;
    });
    return result;
  }

  /** Read-only mirror of {@link connectOneSourceEntry} — same checks, no mutations. */
  private dryRunOneSource(connection: Connection, target: { nodeId: NodeId; handleId?: string }): SourceConnectStatus {
    if (!connection.nodeId || !connection.handleId || !connection.handleType || !connection.handleSide) {
      return 'no-slot';
    }
    if (connection.nodeId === target.nodeId) return 'cycle';

    if (connection.handleSide === 'source') {
      if (this.graph.wouldConnectionResultInCycle(connection.nodeId, target.nodeId)) return 'cycle';
      if (target.handleId) {
        const handleKey = parseHandleKey(target.handleId, target.nodeId, 'input');
        if (!handleKey || !this.isInputCompatible(target.nodeId, handleKey, connection.handleType)) {
          return 'no-slot';
        }
        return 'ok';
      }
      return this.canAutoConnectOnDrop(connection.nodeId, target.nodeId, connection.handleType) ? 'ok' : 'no-slot';
    }

    // target-side drag
    if (this.graph.wouldConnectionResultInCycle(target.nodeId, connection.nodeId)) return 'cycle';
    if (target.handleId) {
      const handleKey = parseHandleKey(target.handleId, target.nodeId, 'output');
      if (!handleKey || !this.isOutputCompatible(target.nodeId, handleKey, connection.handleType)) {
        return 'no-slot';
      }
      return 'ok';
    }
    return this.canAutoConnectOnDropFromTarget(connection.nodeId, target.nodeId, connection.handleType)
      ? 'ok'
      : 'no-slot';
  }

  /** Must be called inside `graph.edit`. Returns a status + the undo entry of any change made. */
  private connectOneSourceEntry(
    connection: Connection,
    target: { nodeId: NodeId; handleId?: string },
  ): { status: SourceConnectStatus; entry: UndoRedoEntry | null } {
    if (!connection.nodeId || !connection.handleId || !connection.handleType || !connection.handleSide) {
      return { status: 'no-slot', entry: null };
    }
    if (connection.nodeId === target.nodeId) return { status: 'cycle', entry: null };

    if (connection.handleSide === 'source') {
      if (this.graph.wouldConnectionResultInCycle(connection.nodeId, target.nodeId)) {
        return { status: 'cycle', entry: null };
      }
      if (target.handleId) {
        const handleKey = parseHandleKey(target.handleId, target.nodeId, 'input');
        if (!handleKey || !this.isInputCompatible(target.nodeId, handleKey, connection.handleType)) {
          return { status: 'no-slot', entry: null };
        }
        return {
          status: 'ok',
          entry: this.connectEdgeToTarget(connection.nodeId, target.nodeId, connection.handleId, target.handleId),
        };
      }
      const entry = this.autoConnectOnDropEntry(
        connection.nodeId,
        connection.handleId,
        target.nodeId,
        connection.handleType,
      );
      return entry ? { status: 'ok', entry } : { status: 'no-slot', entry: null };
    }

    // target-side drag: target.nodeId becomes the source of the new edge.
    if (this.graph.wouldConnectionResultInCycle(target.nodeId, connection.nodeId)) {
      return { status: 'cycle', entry: null };
    }
    if (target.handleId) {
      const handleKey = parseHandleKey(target.handleId, target.nodeId, 'output');
      if (!handleKey || !this.isOutputCompatible(target.nodeId, handleKey, connection.handleType)) {
        return { status: 'no-slot', entry: null };
      }
      return {
        status: 'ok',
        entry: this.connectEdgeToTarget(target.nodeId, connection.nodeId, target.handleId, connection.handleId),
      };
    }
    // The dragged-from node holds the input; `target.nodeId` is the dropped-on output node.
    const entry = this.autoConnectOnDropFromTargetEntry(
      connection.nodeId,
      connection.handleId,
      target.nodeId,
      connection.handleType,
    );
    return entry ? { status: 'ok', entry } : { status: 'no-slot', entry: null };
  }

  private findFirstCompatibleOutput(sourceNodeId: NodeId, handleType: HandleType): string | null {
    for (const outputId of this.graph.getOutputIds(sourceNodeId)) {
      if (this.isOutputCompatible(sourceNodeId, outputId, handleType)) return outputId;
    }
    return null;
  }

  private isInputCompatible(nodeId: NodeId, inputId: string, handleType: HandleType): boolean {
    if (handleType === HandleType.Audio && this.graph.rejectsAudioInput(nodeId)) return false;
    if (handleType === HandleType.Any) return true;
    const validTypes = this.graph.nodeValidInputTypes(nodeId, inputId);
    return validTypes.includes(handleType) || validTypes.includes(HandleType.Any);
  }

  private isAutomaticInputCompatible(nodeId: NodeId, inputId: string, handleType: HandleType): boolean {
    if (
      (handleType === HandleType.Image || handleType === HandleType.Any) &&
      (inputId === 'mask' || inputId === 'mask_url' || this.graph.nodeInputType(nodeId, inputId) === HandleType.Mask)
    ) {
      return false;
    }
    return this.isInputCompatible(nodeId, inputId, handleType);
  }

  private isOutputCompatible(nodeId: NodeId, outputId: string, handleType: HandleType): boolean {
    if (handleType === HandleType.Any) return true;
    const outType = this.graph.nodeOutputType(nodeId, outputId);
    return outType === handleType || outType === HandleType.Any;
  }

  /**
   * Finds the first unconnected input handle on the target node that accepts the given handle
   * type. Uses {@link isInputCompatible} so `Any`-typed sources match any handle and handles
   * declaring `[Any]` accept any source — matching the rest of the codebase's compatibility model.
   */
  private findFirstEmptyCompatibleInput(targetNodeId: NodeId, connectionHandleType: HandleType): string | null {
    const inputIds = this.graph.getInputIds(targetNodeId);

    const backgroundFirst = inputIds.includes('background')
      ? ['background', ...inputIds.filter((id) => id !== 'background')]
      : inputIds;

    for (const inputId of backgroundFirst) {
      const isConnected = this.graph.getNodeInput(targetNodeId, inputId) !== undefined;
      if (isConnected) continue;
      if (this.isAutomaticInputCompatible(targetNodeId, inputId, connectionHandleType)) {
        return inputId;
      }
    }

    return null;
  }

  /** Must be called inside `graph.edit`. Adds a new dynamic input handle on a model node and
   *  connects to it; returns the undo entry, or null if the target doesn't accept dynamic inputs. */
  private addDynamicInputEntry(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): UndoRedoEntry | null {
    const targetData = this.graph.store.getNodeDataNullable<ModelBaseNodeData>(targetNodeId);
    if (!targetData) return null;

    const modelName = getModelIdentifier(targetData);
    if (!modelName) return null;

    const modelBaseView = new ModelBaseView(this.graph, targetNodeId);
    const config = findDynamicInputConfig(modelBaseView.getDynamicInputConfigs(modelName), connectionHandleType);
    if (!config) return null;
    if (getModelInputLimitStatus(modelName, targetData.handles.input, config.prefix).reachedLimit) return null;

    const { key, undoEntry } = modelBaseView.addInputHandleWithKey(config);
    const targetHandleId = getHandleId(targetNodeId, 'input', key);
    const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
    undoEntry.add(this.graph.addEdges([newEdge]));
    return undoEntry;
  }

  /** Must be called inside `graph.edit`. Same contract as {@link addDynamicInputEntry}. */
  private addVideoConcatInputEntry(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): UndoRedoEntry | null {
    if (!this.canAddVideoConcatInput(targetNodeId, connectionHandleType)) return null;

    const view = new VideoConcatenatorView(this.graph, targetNodeId);
    const { key, undoEntry } = view.addInputHandleWithKey();
    const targetHandleId = getHandleId(targetNodeId, 'input', key);
    const edge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
    undoEntry.add(this.graph.addEdges([edge]));
    return undoEntry;
  }

  /** Must be called inside `graph.edit`. Same contract as {@link addDynamicInputEntry}. */
  private addCompositorLayerEntry(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): UndoRedoEntry | null {
    const targetNode = this.graph.store.getNodeNullable(targetNodeId);
    if (!targetNode || targetNode.type !== 'compv3') return null;

    const inputIds = this.graph.getInputIds(targetNodeId);
    if (inputIds.length === 0) return null;

    const validTypes = this.graph.nodeValidInputTypes(targetNodeId, inputIds[0]);
    if (!validTypes.includes(connectionHandleType)) return null;

    const compositorView = new CompositorView(this.graph, targetNodeId);
    const { key, undoEntry } = compositorView.addNodeInputHandleWithKey();
    const targetHandleId = getHandleId(targetNodeId, 'input', key);
    const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
    undoEntry.add(this.graph.addEdges([newEdge]));
    return undoEntry;
  }

  /** Must be called inside `graph.edit`. Same contract as {@link addDynamicInputEntry}. */
  private addPromptVariableEntry(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): UndoRedoEntry | null {
    // Every path that adds a variable comes through here, the multi-connect siblings included.
    if (!this.canAddPromptVariable(targetNodeId, connectionHandleType)) return null;

    const promptView = new PromptView(this.graph, targetNodeId);
    const { key, undoEntry } = promptView.addInputHandleWithVariable();
    const targetHandleId = getHandleId(targetNodeId, 'input', key);
    const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
    undoEntry.add(this.graph.addEdges([newEdge]));
    return undoEntry;
  }

  /** Must be called inside `graph.edit`. Same contract as {@link addDynamicInputEntry}. */
  private addFigmaVariableInputEntry(
    sourceNodeId: NodeId,
    sourceHandleId: string,
    targetNodeId: NodeId,
    connectionHandleType: HandleType,
  ): UndoRedoEntry | null {
    if (!this.canAddFigmaVariableInput(targetNodeId, connectionHandleType)) return null;

    const figmaNodeView = new FigmaNodeView(this.graph, targetNodeId);
    const { key, undoEntry } = figmaNodeView.addVariableInputEntry(connectionHandleType);
    const targetHandleId = getHandleId(targetNodeId, 'input', key);
    const newEdge = this.graph.createEdge(sourceNodeId, targetNodeId, sourceHandleId, targetHandleId);
    undoEntry.add(this.graph.addEdges([newEdge]));
    requestFigmaNodeLayerPickerForTargetInput({
      graph: this.graph,
      onAutoOpen: () => {
        this.selectedNodes = [targetNodeId];
      },
      targetNodeId,
      targetHandleId,
    });
    return undoEntry;
  }
}

export type InvalidConnectionOptions = {
  errorId: 'self-connection' | 'cyclic';
  source: NodeId;
  sourceHandle: string | null;
  target: NodeId;
  targetHandle: string | null;
  additionalParams?: Record<string, unknown>;
};
